import { Box, Button, Grid, Typography } from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { useNavigate } from "react-router-dom";
import { FaFacebook, FaTiktok, FaWhatsapp, FaYoutube } from "react-icons/fa";
import { Images } from "../../../helpers/Images";
import "./Footer.css";

const socialLinks = [
  {
    label: "Facebook",
    href: "https://www.facebook.com/WedoChallengeEgy?mibextid=ZbWKwL",
    Icon: FaFacebook,
  },
  {
    label: "WhatsApp",
    href: "https://wa.me/+201044664265",
    Icon: FaWhatsapp,
  },
  {
    label: "TikTok",
    href: "https://www.tiktok.com/@wedo_challenge?_t=8sgG40At7nc&_r=1",
    Icon: FaTiktok,
  },
  {
    label: "YouTube",
    href: "https://www.youtube.com/@WeDoChallenge",
    Icon: FaYoutube,
  },
];

const exploreLinks = [
  { label: "Home", path: "/" },
  { label: "Categories", path: "/categories" },
  { label: "Gallery", path: "/gallery" },
  { label: "Services", path: "/services" },
  { label: "History", path: "/history" },
  { label: "Zeko Run", path: "/game" },
];

const companyLinks = [
  { label: "Registration", path: "/firststage" },
  { label: "Contact", path: "/contact" },
  { label: "About WDC", path: "/about" },
];

const legalLinks = [
  { label: "Privacy Policy", path: "/privacy" },
  { label: "Terms & Conditions", path: "/terms" },
  { label: "Refund Policy", path: "/refund" },
];

const FooterLink = ({ label, onClick }) => (
  <Typography className="footer-link" onClick={onClick}>
    {label}
  </Typography>
);

const Footer = () => {
  const navigate = useNavigate();
  const year = new Date().getFullYear();

  return (
    <Box
      component="footer"
      sx={{
        width: "100%",
        position: "relative",
        overflow: "hidden",
        background: "linear-gradient(180deg, #2b2e33 0%, #1a1c20 100%)",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          background:
            "radial-gradient(600px 320px at 8% 0%, rgba(193, 26, 131, 0.18), transparent 60%), radial-gradient(500px 280px at 92% 100%, rgba(35, 174, 244, 0.12), transparent 55%)",
        }}
      />

      <Box
        sx={{
          position: "relative",
          maxWidth: "1500px",
          margin: "0 auto",
          px: { xs: "20px", sm: "40px", lg: "80px" },
          py: { xs: "48px", md: "64px" },
        }}
      >
        <Grid container spacing={{ xs: 4, md: 6 }}>
          <Grid item xs={12} md={4}>
            <Box
              component="img"
              src={Images.codeLogoHor}
              alt="We Do Challenge Championship"
              onClick={() => navigate("/")}
              sx={{
                height: { xs: "44px", md: "50px" },
                cursor: "pointer",
                mb: 2,
                display: "block",
              }}
            />
            <Typography
              sx={{
                color: "rgba(255, 255, 255, 0.72)",
                fontSize: { xs: "14px", md: "15px" },
                lineHeight: 1.7,
                maxWidth: "320px",
                mb: 3,
              }}
            >
              Inspiring young minds through robotics, STEAM learning, and
              teamwork at the We Do Challenge championship.
            </Typography>

            <Button
              variant="contained"
              onClick={() => navigate("/firststage")}
              endIcon={<ArrowForwardIcon />}
              sx={{
                borderRadius: "999px",
                textTransform: "none",
                fontWeight: 700,
                fontSize: "14px",
                px: 2.5,
                py: 1,
                color: "#fff",
                background:
                  "linear-gradient(90deg, #23aef4 0%, #c11a83 100%)",
                boxShadow: "0 8px 20px rgba(193, 26, 131, 0.25)",
                "&:hover": {
                  background:
                    "linear-gradient(90deg, #1e9de0 0%, #a8156e 100%)",
                },
              }}
            >
              Register now
            </Button>
          </Grid>

          <Grid item xs={6} sm={4} md={2.5}>
            <Typography className="footer-heading">Explore</Typography>
            {exploreLinks.map((link) => (
              <FooterLink
                key={link.path}
                label={link.label}
                onClick={() => navigate(link.path)}
              />
            ))}
          </Grid>

          <Grid item xs={6} sm={4} md={2.5}>
            <Typography className="footer-heading">Company</Typography>
            {companyLinks.map((link) => (
              <FooterLink
                key={link.path}
                label={link.label}
                onClick={() => navigate(link.path)}
              />
            ))}
          </Grid>

          <Grid item xs={12} sm={4} md={3}>
            <Typography className="footer-heading">Stay connected</Typography>
            <Typography
              sx={{
                color: "rgba(255, 255, 255, 0.65)",
                fontSize: "14px",
                lineHeight: 1.6,
                mb: 2,
              }}
            >
              Follow us for updates, highlights, and championship news.
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              {socialLinks.map(({ label, href, Icon }) => (
                <Box
                  key={label}
                  component="a"
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  className="footer-social-link"
                >
                  <Icon />
                </Box>
              ))}
            </Box>
          </Grid>
        </Grid>

        <Box
          sx={{
            mt: { xs: 5, md: 6 },
            pt: 3,
            borderTop: "1px solid rgba(255, 255, 255, 0.10)",
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: { xs: "flex-start", md: "center" },
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Typography
            sx={{
              color: "rgba(255, 255, 255, 0.55)",
              fontSize: "14px",
              fontWeight: 400,
            }}
          >
            WeDoChallenge© {year} All rights reserved.
          </Typography>

          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: { xs: 1.5, md: 2.5 },
            }}
          >
            {legalLinks.map((link) => (
              <Typography
                key={link.path}
                className="footer-legal-link"
                onClick={() => navigate(link.path)}
              >
                {link.label}
              </Typography>
            ))}
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Typography
              sx={{
                color: "rgba(255, 255, 255, 0.55)",
                fontSize: "14px",
              }}
            >
              Powered by
            </Typography>
            <Box
              component="img"
              src={Images.pulsonic}
              alt="Pulsonic"
              sx={{ height: "20px" }}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Footer;
